import { useCallback, useEffect, useState } from "react";

import { gmao } from "@/api";
import { hasPageAccess } from "@/lib/accessPermissions";
import {
  selectCurrentServiceEvent,
  selectNextMaintenance,
} from "@/lib/gmaoMachineLifecycle";
import { formatApiError } from "@/lib/api";

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function useMachineIdentityDetail(machineId, user) {
  const [machine, setMachine] = useState(null);
  const [accessories, setAccessories] = useState([]);
  const [nextMaintenance, setNextMaintenance] = useState(null);
  const [currentServiceEvent, setCurrentServiceEvent] = useState(null);
  const [maintenanceAvailable, setMaintenanceAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const machineData = await gmao.getMachine(machineId);
      setMachine(machineData);

      const canReadMaintenance = hasPageAccess(
        user,
        "gmao",
        "maintenance"
      );

      const [accessoryResult, interventionResult, maintenanceResult] =
        await Promise.allSettled([
          gmao.listMachineAccessories(machineId),
          gmao.listInterventions({ machine_id: machineId, limit: 100 }),
          canReadMaintenance
            ? gmao.listMaintenancePlans({ machine_id: machineId, limit: 20 })
            : Promise.resolve([]),
        ]);

      setAccessories(
        accessoryResult.status === "fulfilled"
          ? safeArray(accessoryResult.value)
          : []
      );

      const interventions =
        interventionResult.status === "fulfilled"
          ? safeArray(interventionResult.value)
          : [];
      setCurrentServiceEvent(selectCurrentServiceEvent(interventions));

      if (!canReadMaintenance) {
        setMaintenanceAvailable(false);
        setNextMaintenance(null);
      } else if (maintenanceResult.status === "fulfilled") {
        setMaintenanceAvailable(true);
        setNextMaintenance(
          selectNextMaintenance(safeArray(maintenanceResult.value))
        );
      } else {
        setMaintenanceAvailable(false);
        setNextMaintenance(null);
      }
    } catch (requestError) {
      setMachine(null);
      setAccessories([]);
      setNextMaintenance(null);
      setCurrentServiceEvent(null);
      setError(
        formatApiError(requestError.response?.data?.detail) ||
          "Impossible de charger la machine."
      );
    } finally {
      setLoading(false);
    }
  }, [machineId, user]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    machine,
    accessories,
    nextMaintenance,
    currentServiceEvent,
    maintenanceAvailable,
    loading,
    error,
    reload: load,
  };
}
